// src\themes\lino\components\CustomButton.tsx
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';

import { Button } from '@/components/ui/button';
import { cn } from '@/core/lib/utils';

// lino — دکمه‌ی اصلی سایت: گوشه‌های نرم (نه کاملاً تیز و نه کپسولی)،
// هماهنگ با رادیوس بقیه‌ی ویجت‌ها و اینپوت‌های تم (rounded-2xl)،
// حاشیه‌ی مویی، حروف بزرگ با فاصله‌ی حروفِ باز، و در هاور به‌جای
// اسکیل، معکوسِ رنگ (invert) اتفاق می‌افته.
const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-2xl text-[11px] uppercase tracking-[0.16em] font-medium transition-colors duration-300 ease-out focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary-900 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-40',
  {
    variants: {
      variant: {
        default:
          'bg-primary-900 text-primary-0 border border-primary-900 hover:bg-primary-0 hover:text-primary-900',
        secondary:
          'bg-primary-100 text-primary-900 border border-transparent hover:bg-primary-200',
        outline:
          'border border-primary-900 bg-transparent text-primary-900 hover:bg-primary-900 hover:text-primary-0',
        ghost:
          'shadow-none bg-transparent text-primary-900 hover:bg-primary-100 tracking-[0.12em]',
        black:
          'bg-primary-900 border border-primary-900 text-primary-0 hover:bg-primary-700',
        transparent:
          'bg-transparent text-primary-900 hover:bg-primary-100/60 tracking-[0.12em]',
        transparentWhite:
          'bg-transparent border border-primary-0/70 text-primary-0 hover:bg-primary-0 hover:text-primary-900',
      },
      size: {
        sm: 'h-9 px-4 py-2 text-[10px] rounded-xl',
        default: 'h-12 px-7 py-2 text-[11px]',
        lg: 'h-14 text-[12px] px-9 py-2',
        full: 'h-12 text-[11px] px-6 py-2 w-full',
        icon: 'size-10 rounded-xl',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

type CustomButtonProps = {
  loading?: boolean;
  children: ReactNode;
  asChild?: boolean;
} & VariantProps<typeof buttonVariants> &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'size'>;

export default function CustomButton({
  loading = false,
  children,
  className,
  variant = 'default',
  size = 'default',
  disabled,
  type = 'button',
  asChild = false,
  ...props
}: CustomButtonProps) {
  const Comp = asChild ? Slot : Button;

  return (
    <Comp
      type={type}
      disabled={loading || disabled}
      className={cn(buttonVariants({ variant, size }), className)}
      {...props}
    >
      {loading ? <Loader2 className="animate-spin size-4" /> : children}
    </Comp>
  );
}